// --- UPDATE (FlowSense AI Explainer + real-data pass)
//   • A non-array response is read with asList() instead of being stored as the data.
//   • Live: refetches when work orders are created / closed (work_order_events), debounced.
//   • Clicking a bar calls the new optional onAgeGroupClick(ageGroup) prop.
//   • "Explain the backlog" FlowSense AI explainer: how much sits in the oldest bucket, the peak
//     bucket and the average age the server reports — plus a plain-language AI explanation.
// --- END UPDATE
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Flex,
  Text,
  useColorModeValue,
  Button,
  HStack,
  VStack,
  Badge,
  IconButton,
  Tooltip,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Switch,
  FormControl,
  FormLabel,
  Collapse,
  Code,
  Divider,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Select,
  Tag,
  TagLabel,
} from '@chakra-ui/react';
import {
  ComposedChart,
  BarChart,
  LineChart,
  AreaChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  MdRefresh,
  MdDownload,
  MdSettings,
  MdBugReport,
  MdFullscreen,
  MdFullscreenExit,
} from 'react-icons/md';
import { FaChartBar, FaChartLine, FaChartArea, FaLayerGroup } from 'react-icons/fa';
import Card from '../card/Cards';

// --- CRITICAL UPDATE: Swap to Analytics Service to prevent 10k data dump ---
import analyticsService, { asList } from '../../services/analyticsService';
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer'; // --- UPDATE
import useLiveRefresh from '../../hooks/useLiveRefresh';       // --- UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// Custom Tooltip for Recharts
// ─────────────────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, cardBg, borderColor }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="md"
        px={3}
        py={2}
        boxShadow="lg"
        fontSize="sm"
      >
        <Text fontWeight="bold" mb={1}>{label}</Text>
        {payload.map((entry, i) => (
          <Flex key={i} align="center" gap={1}>
            <Box w={2} h={2} borderRadius="full" bg={entry.color} />
            <Text color="gray.500">{entry.name}:</Text>
            <Text fontWeight="semibold">{entry.value} WOs</Text>
          </Flex>
        ))}
      </Box>
    );
  }
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Aging color bands — older age groups turn progressively red
// ─────────────────────────────────────────────────────────────────────────────
const AGING_COLORS = ['#00C49F', '#82ca9d', '#FFB347', '#FF8042', '#E53E3E'];

const getAgingColor = (index, total) => {
  const ratio = total <= 1 ? 0 : index / (total - 1);
  const band = Math.min(Math.floor(ratio * AGING_COLORS.length), AGING_COLORS.length - 1);
  return AGING_COLORS[band];
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const WorkOrderBacklogAging = ({
  data: externalData,
  isLoading: externalLoading,
  enableDebug = false,
  enableExport = true,
  historicalData = null,
  onAgeGroupClick, // --- UPDATE: optional drill-down
}) => {
  // ── Theme ──────────────────────────────────────────────────────────────────
  const chartTextColor = useColorModeValue('#1A202C', '#FFFFFF');
  const cardBg         = useColorModeValue('white', 'gray.800');
  const borderColor    = useColorModeValue('gray.200', 'gray.600');
  const debugBg        = useColorModeValue('gray.50', 'gray.700');
  const gridColor      = useColorModeValue('#E2E8F0', '#4A5568');

  // ── State ──────────────────────────────────────────────────────────────────
  const [chartType,          setChartType]          = useState('composed');
  const [showDebug,          setShowDebug]          = useState(enableDebug);
  const [debugLogs,          setDebugLogs]          = useState([]);
  const [isFullscreen,       setIsFullscreen]       = useState(false);
  const [selectedAgeGroup,   setSelectedAgeGroup]   = useState(null);
  const [showDataLabels,     setShowDataLabels]     = useState(true);
  const [showTrendLine,      setShowTrendLine]      = useState(true);
  const [showAgeColors,      setShowAgeColors]      = useState(true);
  const [showGrid,           setShowGrid]           = useState(true);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    renderTime: 0,
    dataPoints: 0,
    lastUpdate: null,
  });

  // ── 🚀 ADVANCED DEBUGGING: Render Tracking ────────────────────────────────
  const renderCount = useRef(0);
  renderCount.current += 1;

  // ── Internal data fetching ────────────────────────────────────────────────
  const [internalData,    setInternalData]    = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);

  // Determine active data source (Props override internal state if provided)
  const activeData    = externalData || internalData;
  const activeLoading = externalLoading !== undefined ? externalLoading : internalLoading;

  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();
  const toast = useToast();

  // ── 🚀 ADVANCED DEBUGGING: Enhanced Debug Logger ──────────────────────────
  const addDebugLog = useCallback((message, type = 'info', details = null) => {
    if (!enableDebug && !showDebug) return;

    const timestamp = new Date().toISOString();

    let safeDetails = details;
    try {
      if (details instanceof Error) {
        safeDetails = { message: details.message, name: details.name, stack: details.stack };
      } else if (typeof details === 'object' && details !== null) {
        safeDetails = JSON.parse(JSON.stringify(details, (key, value) =>
          typeof value === 'function' ? '[Function]' : value
        ));
      }
    } catch (e) {
      safeDetails = '[Unserializable Object - See Browser Console]';
    }

    const log = { timestamp, type, message, details: safeDetails };
    setDebugLogs(prev => [log, ...prev].slice(0, 100));

    if (type === 'error') {
      console.error(`[BacklogAging] ❌ ${message}`, details);
      console.trace();
    } else if (type === 'warn') {
      console.warn(`[BacklogAging] ⚠️ ${message}`, details);
    } else {
      console.log(`[BacklogAging] ℹ️ ${message}`, details);
    }
  }, [enableDebug, showDebug]);

  // ── 🚀 ADVANCED DEBUGGING: Lifecycle Monitors ────────────────────────────
  useEffect(() => {
    if (renderCount.current % 5 === 0) {
      addDebugLog(`Component Rendered Cycle (Count: ${renderCount.current})`, 'info', {
        chartType,
        activeLoading,
      });
    }
  });

  useEffect(() => {
    addDebugLog('Input Data Source Changed', 'info', {
      hasExternalData: !!externalData,
      internalDataExists: !!internalData,
      activeDataIsArray: Array.isArray(activeData),
      activeDataSize: activeData ? activeData.length : 0,
      sampleHead: Array.isArray(activeData) ? activeData.slice(0, 2) : 'Not an Array',
    });
  }, [activeData, externalData, internalData, addDebugLog]);

  // ── 🚀 CORE LOGIC: Fetch Aggregated Data — DO NOT MODIFY ──────────────────
  const fetchBacklogData = useCallback(async (forceRefresh = false) => {
    if (externalData) {
      addDebugLog('fetchBacklogData skipped: externalData prop is managing state', 'warn');
      return;
    }

    setInternalLoading(true);
    const startTime = performance.now();
    addDebugLog(`Initiating data fetch (forceRefresh: ${forceRefresh})`, 'info');

    try {
      const response = await analyticsService.getWidgetData('backlog-aging');
      const parsedData = asList(response); // --- UPDATE: always an array

      addDebugLog('Raw payload received from API', 'info', {
        payloadType: typeof parsedData,
        isArray: Array.isArray(parsedData),
      });

      setInternalData(parsedData);

      const fetchTime = performance.now() - startTime;
      addDebugLog('Backlog aging data fetched successfully', 'success', {
        ms: fetchTime.toFixed(2),
      });

      if (forceRefresh) {
        toast({
          title: 'Data Refreshed',
          description: 'Synced backlog aging data from database.',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      addDebugLog(`Fetch failed: ${error.message}`, 'error', error);
      toast({
        title: 'Error fetching backlog data',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setInternalLoading(false);
    }
  }, [externalData, addDebugLog, toast]);

  // Initial load
  useEffect(() => {
    fetchBacklogData();
  }, [fetchBacklogData]);

  // --- UPDATE: live refresh
  useLiveRefresh(['work_order_events'], () => fetchBacklogData(false), { enabled: !externalData, accept: (b) => !b?.type || ['created', 'updated', 'status_updated', 'assigned', 'completed', 'closed', 'deleted', 'priority_changed'].includes(b.type) });
  const pickAge = useCallback((g) => { setSelectedAgeGroup(g); if (onAgeGroupClick) onAgeGroupClick(g); }, [onAgeGroupClick]);
  // --- END UPDATE

  // ── Normalized Data ───────────────────────────────────────────────────────
  // Validates shape; enriches each row with a color band and percentage
  const normalizedData = useMemo(() => {
    const startTime = performance.now();

    if (!activeData || !Array.isArray(activeData) || activeData.length === 0) {
      addDebugLog('NormalizedData aborted: activeData is empty or not an array', 'warn');
      return null;
    }

    const total = activeData.reduce((sum, item) => sum + (Number(item.count) || 0), 0);

    const result = activeData.map((item, index) => ({
      age_group:  item.age_group  || item.ageGroup || item.label || `Group ${index + 1}`,
      count:      Number(item.count)      || 0,
      avg_days:   Number(item.avg_days)   || 0,
      percentage: total > 0 ? ((Number(item.count) || 0) / total * 100).toFixed(1) : '0.0',
      color:      getAgingColor(index, activeData.length),
    }));

    const renderTime = performance.now() - startTime;
    setPerformanceMetrics({
      renderTime: renderTime.toFixed(2),
      dataPoints: result.length,
      lastUpdate: new Date(),
    });

    addDebugLog('NormalizedData computed', 'success', {
      rows: result.length,
      total,
      ms: renderTime.toFixed(2),
    });

    return result;
  }, [activeData, addDebugLog]);

  // Trace normalizer
  useEffect(() => {
    addDebugLog('Normalized Data Evaluated', 'success', { output: normalizedData });
  }, [normalizedData, addDebugLog]);

  // ── Derived Summary ───────────────────────────────────────────────────────
  const summary = useMemo(() => {
    if (!normalizedData) return null;

    const total        = normalizedData.reduce((s, i) => s + i.count, 0);
    const criticalItem = normalizedData[normalizedData.length - 1]; // oldest bucket
    const freshItem    = normalizedData[0];                         // newest bucket
    const maxItem      = normalizedData.reduce((a, b) => (b.count > a.count ? b : a), normalizedData[0]);

    // Trend vs historical (if provided)
    let trend = null;
    if (historicalData && Array.isArray(historicalData)) {
      const historicalTotal = historicalData.reduce((s, i) => s + (Number(i.count) || 0), 0);
      if (historicalTotal > 0) {
        const change = ((total - historicalTotal) / historicalTotal) * 100;
        trend = {
          direction: change > 0 ? 'increase' : change < 0 ? 'decrease' : 'unchanged',
          percentage: Math.abs(change).toFixed(1),
        };
      }
    }

    return { total, criticalItem, freshItem, maxItem, trend };
  }, [normalizedData, historicalData]);

  // --- UPDATE: FlowSense rule findings
  const agingExplainer = useMemo(() => {
    const findings = [];
    if (!normalizedData || !summary) return { findings, facts: {} };
    const { total, criticalItem, maxItem, freshItem } = summary;
    if (total > 0 && criticalItem && normalizedData.length > 1) {
      const share = (criticalItem.count / total) * 100;
      findings.push({ severity: share >= 25 ? 'high' : share >= 10 ? 'medium' : 'info', text: `${criticalItem.count} work orders (${share.toFixed(0)}% of the backlog) are in the oldest bucket, ${criticalItem.age_group}.`, evidence: criticalItem.avg_days ? `average age ${criticalItem.avg_days.toFixed(0)} days` : undefined });
    }
    if (maxItem && maxItem !== freshItem && maxItem.count > 0) findings.push({ severity: 'medium', text: `The largest bucket is ${maxItem.age_group} (${maxItem.count}), not the newest — work is piling up rather than being cleared.` });
    if (summary.trend) findings.push({ severity: summary.trend.direction === 'increase' ? 'medium' : 'ok', text: `Backlog is ${summary.trend.direction === 'increase' ? 'up' : 'down'} ${summary.trend.percentage}% against the comparison period.` });
    if (total === 0) findings.push({ severity: 'ok', text: 'No open work orders in the backlog.' });
    return { findings, facts: { total_backlog: total, buckets: normalizedData.map(b => ({ age_group: b.age_group, count: b.count, avg_days: b.avg_days || null, share_pct: Number(b.percentage) })), trend: summary.trend } };
  }, [normalizedData, summary]);
  // --- END UPDATE

  // ── Export Functionality ──────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    if (!normalizedData) return;

    try {
      const exportData = {
        timestamp: new Date().toISOString(),
        chartType,
        data: normalizedData,
        summary: {
          total:        summary?.total,
          oldestBucket: summary?.criticalItem?.age_group,
          peakBucket:   summary?.maxItem?.age_group,
        },
      };

      const dataStr            = JSON.stringify(exportData, null, 2);
      const dataUri            = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      const exportFileName     = `backlog-aging-${Date.now()}.json`;
      const link               = document.createElement('a');
      link.setAttribute('href', dataUri);
      link.setAttribute('download', exportFileName);
      link.click();

      addDebugLog('Data exported successfully', 'success');
      toast({
        title: 'Export Complete',
        description: 'Backlog aging data has been exported.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      addDebugLog(`Export failed: ${error.message}`, 'error', error);
      toast({
        title: 'Export Failed',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [normalizedData, summary, chartType, addDebugLog, toast]);

  // ── Custom Label for Recharts Bars ────────────────────────────────────────
  const renderCustomLabel = ({ x, y, width, value, index }) => {
    if (!showDataLabels || value === 0) return null;
    return (
      <text
        x={x + width / 2}
        y={y - 6}
        fill={chartTextColor}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
      >
        {value}
      </text>
    );
  };

  // ── Stats Row ─────────────────────────────────────────────────────────────
  const renderStats = () => {
    if (!normalizedData || !summary) return null;

    const statItems = [
      {
        label:  'Total Backlog',
        value:  summary.total,
        help:   `${normalizedData.length} age groups`,
        scheme: 'blue',
        trend:  summary.trend,
      },
      {
        label:  'Freshest',
        value:  summary.freshItem?.count ?? 0,
        help:   summary.freshItem?.age_group ?? '—',
        scheme: 'green',
      },
      {
        label:  'Peak Volume',
        value:  summary.maxItem?.count ?? 0,
        help:   summary.maxItem?.age_group ?? '—',
        scheme: 'orange',
      },
      {
        label:  'Oldest Bucket',
        value:  summary.criticalItem?.count ?? 0,
        help:   summary.criticalItem?.age_group ?? '—',
        scheme: 'red',
      },
    ];

    return (
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mb={4}>
        {statItems.map((item) => (
          <Stat
            key={item.label}
            size="sm"
            p={2}
            borderRadius="md"
            borderWidth="1px"
            borderColor={borderColor}
            bg={cardBg}
            transition="all 0.2s"
            _hover={{ borderColor: `${item.scheme}.400`, transform: 'translateY(-2px)' }}
          >
            <StatLabel fontSize="xs" color="gray.500">{item.label}</StatLabel>
            <StatNumber fontSize="lg">
              <Badge colorScheme={item.scheme} variant="subtle" fontSize="md" px={1}>
                {item.value}
              </Badge>
            </StatNumber>
            <StatHelpText fontSize="xs" mb={0}>
              {item.trend ? (
                <>
                  <StatArrow type={item.trend.direction === 'increase' ? 'increase' : 'decrease'} />
                  {item.trend.percentage}% vs prev
                </>
              ) : (
                item.help
              )}
            </StatHelpText>
          </Stat>
        ))}
      </SimpleGrid>
    );
  };

  // ── 🚀 CORE LOGIC: Chart Renderer — DO NOT MODIFY DATA KEYS ──────────────
  // Only chart-type selection wraps the original ComposedChart logic.
  const renderChart = () => {
    const height = isFullscreen ? 500 : 300;

    if (!normalizedData || normalizedData.length === 0) {
      return (
        <Flex justify="center" align="center" h={`${height}px`} direction="column">
          <Text color="gray.500" fontSize="lg" mb={2}>No backlog aging data available</Text>
          <Text color="gray.400" fontSize="sm">Check the analytics service connection.</Text>
        </Flex>
      );
    }

    const commonProps = {
      data: normalizedData,
      margin: { top: 10, right: 24, left: 0, bottom: 4 },
    };

    const xAxis = (
      <XAxis
        dataKey="age_group"
        stroke={chartTextColor}
        tick={{ fontSize: 12, fontWeight: 600 }}
        tickLine={false}
        axisLine={{ stroke: gridColor }}
      />
    );

    const yAxis = (
      <YAxis
        stroke={chartTextColor}
        tick={{ fontSize: 11 }}
        tickLine={false}
        axisLine={false}
        width={36}
      />
    );

    const grid = showGrid && (
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />
    );

    const tooltip = (
      <RechartsTooltip
        content={<CustomTooltip cardBg={cardBg} borderColor={borderColor} />}
        cursor={{ fill: 'rgba(130,130,200,0.08)' }}
      />
    );

    const bars = normalizedData.map((entry, index) => (
      <Cell
        key={`cell-${index}`}
        fill={showAgeColors ? entry.color : '#FF8042'}
        onClick={() => setSelectedAgeGroup(entry.age_group)}
      />
    ));

    // ── Composed Chart (default) — ORIGINAL CORE LOGIC ──────────────────
    if (chartType === 'composed') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart {...commonProps}>
            {grid}
            {xAxis}
            {yAxis}
            {tooltip}
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Bar
              dataKey="count"
              name="Work Orders"
              radius={[4, 4, 0, 0]}
              label={showDataLabels ? renderCustomLabel : false}
              isAnimationActive
            >
              {normalizedData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={showAgeColors ? normalizedData[index].color : '#FF8042'}
                  cursor="pointer"
                  onClick={() => pickAge(normalizedData[index].age_group)}
                />
              ))}
            </Bar>
            {showTrendLine && (
              <Line
                type="monotone"
                dataKey="count"
                stroke="#8884d8"
                strokeWidth={3}
                dot={{ r: 5, fill: '#8884d8', strokeWidth: 2, stroke: '#fff' }}
                activeDot={{ r: 7 }}
                name="Trend"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      );
    }

    // ── Bar Only ──────────────────────────────────────────────────────────
    if (chartType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart {...commonProps}>
            {grid}
            {xAxis}
            {yAxis}
            {tooltip}
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Bar
              dataKey="count"
              name="Work Orders"
              radius={[4, 4, 0, 0]}
              label={showDataLabels ? renderCustomLabel : false}
              isAnimationActive
            >
              {normalizedData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={showAgeColors ? normalizedData[index].color : '#FF8042'}
                  cursor="pointer"
                  onClick={() => pickAge(normalizedData[index].age_group)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    // ── Line Only ─────────────────────────────────────────────────────────
    if (chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart {...commonProps}>
            {grid}
            {xAxis}
            {yAxis}
            {tooltip}
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#FF8042"
              strokeWidth={3}
              dot={{ r: 5, fill: '#FF8042', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 7 }}
              name="Work Orders"
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    // ── Area Chart ────────────────────────────────────────────────────────
    if (chartType === 'area') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="agingGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#FF8042" stopOpacity={0.6} />
                <stop offset="95%" stopColor="#FF8042" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            {grid}
            {xAxis}
            {yAxis}
            {tooltip}
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Area
              type="monotone"
              dataKey="count"
              stroke="#FF8042"
              strokeWidth={3}
              fill="url(#agingGradient)"
              dot={{ r: 5, fill: '#FF8042', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 7 }}
              name="Work Orders"
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    return null;
  };

  // ── Debug Console ─────────────────────────────────────────────────────────
  const renderDebugConsole = () => (
    <Collapse in={showDebug} animateOpacity>
      <Box
        mt={4}
        p={3}
        bg={debugBg}
        borderRadius="md"
        borderWidth="1px"
        borderColor={borderColor}
        maxH="300px"
        overflowY="auto"
      >
        <HStack justify="space-between" mb={2}>
          <Text fontWeight="bold" fontSize="sm">Advanced Debug Console</Text>
          <HStack>
            <Badge colorScheme="blue">Renders: {renderCount.current}</Badge>
            <Button size="xs" onClick={() => setDebugLogs([])}>Clear</Button>
          </HStack>
        </HStack>

        {/* Performance Metrics */}
        <Box mb={2} p={2} bg={cardBg} borderRadius="md">
          <Text fontSize="xs" fontWeight="bold" mb={1}>Performance Metrics:</Text>
          <SimpleGrid columns={3} spacing={2} fontSize="xs">
            <Box>
              <Text color="gray.500">Render Time:</Text>
              <Text fontWeight="semibold">{performanceMetrics.renderTime}ms</Text>
            </Box>
            <Box>
              <Text color="gray.500">Data Points:</Text>
              <Text fontWeight="semibold">{performanceMetrics.dataPoints}</Text>
            </Box>
            <Box>
              <Text color="gray.500">Last Update:</Text>
              <Text fontWeight="semibold">
                {performanceMetrics.lastUpdate?.toLocaleTimeString() || 'N/A'}
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        <Divider mb={2} />

        <VStack align="stretch" spacing={2} fontSize="xs">
          {debugLogs.map((log, index) => (
            <Box
              key={index}
              p={2}
              borderRadius="md"
              bg={
                log.type === 'error'   ? 'red.50'    :
                log.type === 'warn'    ? 'yellow.50' :
                log.type === 'success' ? 'green.50'  : 'white'
              }
              borderWidth="1px"
              borderColor={
                log.type === 'error'   ? 'red.200'    :
                log.type === 'warn'    ? 'yellow.200' :
                log.type === 'success' ? 'green.200'  : 'gray.200'
              }
            >
              <Text
                fontWeight="bold"
                color={
                  log.type === 'error'   ? 'red.700'    :
                  log.type === 'warn'    ? 'yellow.700' :
                  log.type === 'success' ? 'green.700'  : 'gray.700'
                }
              >
                [{new Date(log.timestamp).toLocaleTimeString()}] {log.type.toUpperCase()}: {log.message}
              </Text>
              {log.details && (
                <Code
                  mt={1}
                  p={1}
                  w="100%"
                  fontSize="10px"
                  colorScheme="blackAlpha"
                  whiteSpace="pre-wrap"
                  display="block"
                >
                  {JSON.stringify(log.details, null, 2)}
                </Code>
              )}
            </Box>
          ))}
          {debugLogs.length === 0 && (
            <Text color="gray.500" textAlign="center">No logs yet</Text>
          )}
        </VStack>
      </Box>
    </Collapse>
  );

  // ── Main Content ──────────────────────────────────────────────────────────
  const renderContent = () => {
    if (activeLoading) {
      return (
        <Flex justify="center" align="center" h="300px" direction="column">
          <Spinner size="xl" mb={4} color="orange.400" />
          <Text color="gray.500">Loading backlog aging analytics...</Text>
          <Progress size="xs" isIndeterminate w="200px" mt={2} colorScheme="orange" />
        </Flex>
      );
    }

    return (
      <Box>
        {renderStats()}

        {/* Selected age group tag */}
        {selectedAgeGroup && (
          <HStack mb={2}>
            <Text fontSize="xs" color="gray.500">Focused:</Text>
            <Tag
              size="sm"
              colorScheme="orange"
              borderRadius="full"
              cursor="pointer"
              onClick={() => setSelectedAgeGroup(null)}
            >
              <TagLabel>{selectedAgeGroup} ✕</TagLabel>
            </Tag>
          </HStack>
        )}

        {renderChart()}

        {/* --- UPDATE: FlowSense AI explainer */}
        {normalizedData && (
          <Box mt={4}>
            <FlowSenseAIExplainer
              module="backlog_aging"
              title="Explain the backlog"
              subtitle="How old open work orders are"
              facts={agingExplainer.facts}
              findings={agingExplainer.findings}
              compact
              questions={['Which old work orders should be closed or escalated first?', 'Why is the backlog ageing?']}
            />
          </Box>
        )}
        {/* --- END UPDATE */}
      </Box>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Card h="100%" position="relative">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Flex justify="space-between" align="center" mb={4}>
        <HStack>
          <Heading size="md">Work Order Backlog Aging</Heading>
          {summary && (
            <Badge colorScheme="orange" fontSize="sm">
              {summary.total} Total
            </Badge>
          )}
        </HStack>

        <HStack spacing={2}>
          {/* Refresh */}
          {!externalData && (
            <Tooltip label="Refresh Backlog Data">
              <IconButton
                icon={<MdRefresh />}
                size="sm"
                variant="outline"
                isLoading={activeLoading}
                onClick={() => fetchBacklogData(true)}
              />
            </Tooltip>
          )}

          {/* Chart Type Selector */}
          <Menu>
            <Tooltip label="Chart type">
              <MenuButton
                as={IconButton}
                icon={
                  chartType === 'line'     ? <FaChartLine />  :
                  chartType === 'area'     ? <FaChartArea />  :
                  chartType === 'bar'      ? <FaChartBar />   :
                                             <FaLayerGroup />
                }
                size="sm"
                variant="outline"
              />
            </Tooltip>
            <MenuList>
              <MenuItem icon={<FaLayerGroup />} onClick={() => setChartType('composed')}>
                Composed (Bar + Trend)
              </MenuItem>
              <MenuItem icon={<FaChartBar />}   onClick={() => setChartType('bar')}>
                Bar Chart
              </MenuItem>
              <MenuItem icon={<FaChartLine />}  onClick={() => setChartType('line')}>
                Line Chart
              </MenuItem>
              <MenuItem icon={<FaChartArea />}  onClick={() => setChartType('area')}>
                Area Chart
              </MenuItem>
            </MenuList>
          </Menu>

          {/* Export */}
          {enableExport && (
            <Tooltip label="Export data">
              <IconButton
                icon={<MdDownload />}
                size="sm"
                variant="outline"
                onClick={handleExport}
                isDisabled={!normalizedData || activeLoading}
              />
            </Tooltip>
          )}

          {/* Settings */}
          <Tooltip label="Chart settings">
            <IconButton
              icon={<MdSettings />}
              size="sm"
              variant="outline"
              onClick={onSettingsOpen}
            />
          </Tooltip>

          {/* Debug Toggle */}
          {enableDebug && (
            <Tooltip label="Toggle debug console">
              <IconButton
                icon={<MdBugReport />}
                size="sm"
                variant="outline"
                colorScheme={showDebug ? 'red' : 'gray'}
                onClick={() => setShowDebug(!showDebug)}
              />
            </Tooltip>
          )}

          {/* Fullscreen */}
          <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton
              icon={isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
              size="sm"
              variant="outline"
              onClick={() => setIsFullscreen(!isFullscreen)}
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      {renderContent()}

      {/* ── Debug Console ──────────────────────────────────────────────────── */}
      {enableDebug && renderDebugConsole()}

      {/* ── Settings Modal ─────────────────────────────────────────────────── */}
      <Modal isOpen={isSettingsOpen} onClose={onSettingsClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Chart Settings</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Chart Type</FormLabel>
                <Select value={chartType} onChange={(e) => setChartType(e.target.value)}>
                  <option value="composed">Composed (Bar + Trend Line)</option>
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="area">Area Chart</option>
                </Select>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>Show Data Labels</FormLabel>
                <Switch
                  isChecked={showDataLabels}
                  onChange={(e) => setShowDataLabels(e.target.checked)}
                />
              </FormControl>

              {(chartType === 'composed') && (
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>Show Trend Line</FormLabel>
                  <Switch
                    isChecked={showTrendLine}
                    onChange={(e) => setShowTrendLine(e.target.checked)}
                  />
                </FormControl>
              )}

              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>Age-Based Color Bands</FormLabel>
                <Switch
                  isChecked={showAgeColors}
                  onChange={(e) => setShowAgeColors(e.target.checked)}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>Show Grid Lines</FormLabel>
                <Switch
                  isChecked={showGrid}
                  onChange={(e) => setShowGrid(e.target.checked)}
                />
              </FormControl>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default WorkOrderBacklogAging;